/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Network-import consent modal (S7).
 *
 * Full-page overlay shown BEFORE the file picker renders. Requires an
 * explicit checkbox tick + click of "Continue" — the checkbox is not
 * dismissible into a "later" state. Copy is inline, not a link to a
 * policy page.
 *
 * Frontend calls POST /api/network/consent on Continue, then invokes
 * onAccept() so the parent can reveal the file picker. Version bumps
 * are handled server-side (`currentVersion` in GET /api/network/consent
 * response); the modal is shown whenever accepted !== true.
 */

import React, { useState } from 'react';
import api from '../../services/api';

export default function ConsentModal({ onAccept, onCancel }) {
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleContinue = async () => {
    if (!checked || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post('/network/consent', {});
      if (res.success) {
        onAccept();
      } else {
        setError(res.error || 'Could not save consent — please retry.');
      }
    } catch (e) {
      setError(e.message || 'Could not save consent — please retry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Before you upload your network data</h2>
        <p style={{ fontWeight: 600 }}>Read this. It matters.</p>

        <ul style={{ paddingLeft: '20px', lineHeight: 1.6 }}>
          <li>
            We do <strong>not</strong> accept files containing names, emails,
            or LinkedIn URLs. The uploader will reject them.
          </li>
          <li>
            We use only <strong>company + role</strong> to help you find
            mentors and opportunities in your network.
          </li>
          <li>
            The people in your network have <strong>not consented</strong> to
            be added to our platform. Only upload what you are personally
            comfortable sharing.
          </li>
          <li>
            You can <strong>delete all of your network data</strong> at any
            time from Settings.
          </li>
          <li>
            Under India's DPDP Act, you are personally responsible for the
            data you upload about others.
          </li>
        </ul>

        <label style={checkboxRowStyle}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          I understand and confirm my responsibility.
        </label>

        {error && (
          <div style={{ color: '#b91c1c', marginTop: '12px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <div style={buttonRowStyle}>
          <button
            type="button"
            onClick={onCancel}
            style={cancelBtnStyle}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!checked || saving}
            style={{
              ...continueBtnStyle,
              opacity: (!checked || saving) ? 0.5 : 1,
              cursor: (!checked || saving) ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving…' : 'Continue to upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const cardStyle = {
  background: '#fff',
  borderRadius: '12px',
  padding: '32px',
  maxWidth: '560px',
  width: '100%',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

const checkboxRowStyle = {
  display: 'flex',
  alignItems: 'center',
  marginTop: '20px',
  padding: '12px',
  background: '#f8fafc',
  borderRadius: '6px',
  cursor: 'pointer'
};

const buttonRowStyle = {
  display: 'flex',
  gap: '12px',
  marginTop: '24px',
  justifyContent: 'flex-end'
};

const cancelBtnStyle = {
  padding: '10px 20px',
  background: '#fff',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px'
};

const continueBtnStyle = {
  padding: '10px 20px',
  background: '#00BCD4',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 600
};
